/**
 * Importer live-grid rework — the Review step's parks → turbines status tree
 * (replaces the plain commit preamble). The included analyzed rows are grouped
 * by **park (siteName)** → **turbine (identifier)**; each node carries a status
 * chip from the shared review vocabulary:
 *   - **exists**       — the asset/site already exists (matched, no override).
 *   - **will-create**  — new (does not exist).
 *   - **would-change** — an override_block diff against existing data.
 *   - **error**        — a hard_error blocks the row.
 *
 * No new dependency: `@mui/x-tree-view` is not in the workspace (see
 * `detail/ProjectParksTree`), so the tree is a plain nested `List` with
 * collapsible park groups. The commit action ({@link Step5Commit}) renders
 * BELOW the tree (passed as `children`) so the unchanged commit / override /
 * model-confirm paths stay intact.
 */
import { useMemo, useState, type ReactElement, type ReactNode } from "react";
import {
  Box,
  Chip,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import {
  buildReviewTree,
  type ReviewParkNode,
  type ReviewStatus,
  type ReviewTurbineNode,
} from "./mapping.js";
import { diffFieldLabel, diffLine } from "./overrideDiff.js";
import type { AnalyzeImportReport } from "./types.js";
import { ChevronDownIcon, ChevronUpIcon } from "@aeriosiq/ui/icons";

const STATUS_META: Record<
  ReviewStatus,
  { label: string; color: "success" | "info" | "warning" | "error" }
> = {
  exists: { label: "Exists", color: "success" },
  "will-create": { label: "Will create", color: "info" },
  "would-change": { label: "Keep existing", color: "warning" },
  error: { label: "Error", color: "error" },
};

function StatusChip({
  status,
  testid,
}: {
  status: ReviewStatus;
  testid: string;
}): ReactElement {
  const meta = STATUS_META[status];
  return (
    <Chip
      size="small"
      color={meta.color}
      variant="outlined"
      label={meta.label}
      data-testid={testid}
    />
  );
}

export interface ReviewTreeProps {
  report: AnalyzeImportReport;
  /** FB-32 — the `(rowIndex, field)` CELLS a tenant_admin marked to override
   *  (update base data from the sheet), keyed `${rowIndex}:${field}`. A
   *  `would-change` turbine lists WHICH fields will update (per cell) vs which
   *  stay "keep existing". */
  overrideFieldKeys?: ReadonlySet<string>;
  /** The commit action (Step5Commit) — rendered below the tree. */
  children?: ReactNode;
}

export function ReviewTree({
  report,
  overrideFieldKeys,
  children,
}: ReviewTreeProps): ReactElement {
  const parks = useMemo(
    () => buildReviewTree(report, overrideFieldKeys),
    [report, overrideFieldKeys],
  );

  const turbineCount = parks.reduce((n, p) => n + p.turbines.length, 0);

  return (
    <Stack spacing={2} sx={{ minHeight: 320 }}>
      <Box>
        <Typography variant="h6" gutterBottom>
          Review &amp; import
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {`${String(parks.length)} ${
            parks.length === 1 ? "park" : "parks"
          } · ${String(turbineCount)} ${
            turbineCount === 1 ? "turbine" : "turbines"
          } selected for import. Each shows whether it already exists, will be created, would change existing data, or has an error.`}
        </Typography>
      </Box>

      {parks.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="review-tree-empty"
        >
          No rows selected for import.
        </Typography>
      ) : (
        <List
          dense
          disablePadding
          data-testid="review-tree"
          aria-label="Parks and turbines to import"
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "background.paper",
          }}
        >
          {parks.map((park) => (
            <ParkRow key={park.siteName} park={park} />
          ))}
        </List>
      )}

      {children}
    </Stack>
  );
}

function ParkRow({ park }: { park: ReviewParkNode }): ReactElement {
  const [open, setOpen] = useState(true);
  return (
    <Box
      component="li"
      sx={{ listStyle: "none" }}
      data-testid={`park-${park.siteName}`}
    >
      <ListItemButton
        onClick={() => {
          setOpen((o) => !o);
        }}
        sx={{ py: 0.5, gap: 1 }}
        aria-expanded={open}
      >
        {open ? (
          <ChevronUpIcon
            fontSize="small"
            sx={{ mr: 1, color: "text.secondary" }}
          />
        ) : (
          <ChevronDownIcon
            fontSize="small"
            sx={{ mr: 1, color: "text.secondary" }}
          />
        )}
        <ListItemText
          primary={park.siteName}
          slotProps={{ primary: { variant: "subtitle2" } }}
        />
        <StatusChip
          status={park.status}
          testid={`park-status-${park.siteName}`}
        />
        <Chip
          size="small"
          variant="outlined"
          label={`${String(park.turbines.length)} ${
            park.turbines.length === 1 ? "turbine" : "turbines"
          }`}
        />
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List dense disablePadding component="ul">
          {park.turbines.map((t) => (
            <TurbineRow key={t.rowIndex} turbine={t} />
          ))}
        </List>
      </Collapse>
    </Box>
  );
}

function TurbineRow({ turbine }: { turbine: ReviewTurbineNode }): ReactElement {
  return (
    <Box
      component="li"
      sx={{ listStyle: "none", pl: 5, pr: 2, py: 0.5 }}
      data-testid={`turbine-${String(turbine.rowIndex)}`}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography variant="body2" noWrap title={turbine.identifier}>
          {turbine.identifier}
        </Typography>
        {/* FB-32 — a would-change row with one or more cells marked for override
            reads "Will update" (the per-field detail is listed below);
            otherwise the default "Keep existing" status chip. */}
        {turbine.willUpdate ? (
          <Chip
            size="small"
            color="info"
            variant="outlined"
            label="Will update"
            data-testid={`turbine-status-${String(turbine.rowIndex)}`}
          />
        ) : (
          <StatusChip
            status={turbine.status}
            testid={`turbine-status-${String(turbine.rowIndex)}`}
          />
        )}
      </Box>
      {/* FB-27/FB-32: the per-field diff inline on a would-change turbine. Each
          field is tagged "Will update" (admin marked this CELL) or "Keep
          existing" (default) — PER FIELD, not the whole row. */}
      {turbine.status === "would-change" && turbine.diffs.length > 0 ? (
        <Box
          sx={{ pl: 0.5, pt: 0.25 }}
          data-testid={`turbine-diff-${String(turbine.rowIndex)}`}
        >
          {turbine.willUpdateFields.length > 0 ? (
            <Typography
              variant="caption"
              color="info.main"
              component="div"
              sx={{ whiteSpace: "normal", fontWeight: 600 }}
              data-testid={`turbine-will-update-${String(turbine.rowIndex)}`}
            >
              {`Will update: ${turbine.willUpdateFields
                .map((f) => diffFieldLabel(f))
                .join(", ")}`}
            </Typography>
          ) : null}
          {turbine.diffs.map((d) => {
            const willUpdate = turbine.willUpdateFields.includes(d.field);
            return (
              <Typography
                key={d.field}
                variant="caption"
                color={willUpdate ? "info.main" : "warning.main"}
                component="div"
                sx={{ whiteSpace: "normal" }}
              >
                {`${willUpdate ? "✓ update" : "keep existing"} — ${diffLine(d)}`}
              </Typography>
            );
          })}
        </Box>
      ) : null}
      {/* FB-36 — an error turbine is SKIPPED (not imported); make that explicit
          alongside the FB-35 reason. The row stays visible in the tree. */}
      {turbine.status === "error" ? (
        <Box
          sx={{ pl: 0.5, pt: 0.25 }}
          data-testid={`turbine-error-${String(turbine.rowIndex)}`}
        >
          <Chip
            size="small"
            color="error"
            variant="filled"
            label="Will not be imported"
            sx={{ mb: 0.25 }}
            data-testid={`turbine-skipped-${String(turbine.rowIndex)}`}
          />
          {/* FB-35 — show WHY an error turbine is blocked (the hard_error
              reason) under the tag, not just the tag. */}
          {turbine.errorMessages.map((msg, i) => (
            <Typography
              key={i}
              variant="caption"
              color="error.main"
              component="div"
              sx={{ whiteSpace: "normal" }}
            >
              {msg}
            </Typography>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}
